import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { MdChecklist, MdDeleteOutline, MdDragIndicator, MdEvent } from 'react-icons/md'
import TaskMemberAvatar from '../TaskMemberAvatar'
import { getTaskChecklistProgress, getTaskDueDateStatus } from '../taskBoardUtils'
import './task-page-2024-task.css'

const TaskCardPreview = ({ card }) => (
  <div className="task-board-card task-board-card--overlay">
    <strong>{card.title}</strong>
    {card.description && <p>{card.description}</p>}
  </div>
)

const SortableTaskCard = ({ card, disabled, onEdit, onDelete }) => {
  const checklistProgress = getTaskChecklistProgress(card)
  const dueDate = getTaskDueDateStatus(card.dueDate)
  const dueDateLabel = dueDate?.dueDate.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  const sortable = useSortable({
    id: card.id,
    data: { type: 'card', card },
    disabled,
  })
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  }

  if (sortable.isDragging) {
    return <div ref={sortable.setNodeRef} className="task-board-card__placeholder" style={style} />
  }

  return (
    <article
      ref={sortable.setNodeRef}
      style={style}
      className="task-board-card"
      onClick={onEdit}
      {...sortable.attributes}
      {...sortable.listeners}
    >
      <div className="task-board-card__top">
        <MdDragIndicator className="task-board-card__drag" aria-hidden="true" />
        <strong>{card.title}</strong>
        <button
          type="button"
          aria-label={`Delete ${card.title}`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation()
            onDelete()
          }}
          disabled={disabled}
        >
          <MdDeleteOutline />
        </button>
      </div>
      {card.description && <p>{card.description}</p>}
      {(dueDate || checklistProgress.checklistCount > 0 || card.assignedMembers?.length > 0) && (
        <div className="task-board-card__footer">
          {dueDate && (
            <span
              className={`task-board-card__due-date is-${dueDate.status}`}
              aria-label={`Due ${dueDateLabel}`}
              title={dueDate.status === 'overdue' ? `Overdue · ${dueDateLabel}` : `Due ${dueDateLabel}`}
            >
              <MdEvent /> {dueDateLabel}
            </span>
          )}
          {checklistProgress.checklistCount > 0 && (
            <span
              className={`task-board-card__checklist-progress ${checklistProgress.total > 0 && checklistProgress.completed === checklistProgress.total ? 'is-complete' : ''}`}
              title={`${checklistProgress.percentage}% of checklist items complete`}
            >
              <MdChecklist /> {checklistProgress.completed}/{checklistProgress.total}
            </span>
          )}
          {card.assignedMembers?.length > 0 && (
            <div className="task-board-card__members" aria-label="Assigned members">
              {card.assignedMembers.slice(0, 4).map((member) => (
                <TaskMemberAvatar key={member.userId} member={member} size="small" />
              ))}
              {card.assignedMembers.length > 4 && (
                <span className="task-board-card__member-overflow">
                  +{card.assignedMembers.length - 4}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  )
}

const TaskPage2024Task = ({ card, disabled = false, onEdit, onDelete, overlay = false }) => (
  overlay
    ? <TaskCardPreview card={card} />
    : <SortableTaskCard card={card} disabled={disabled} onEdit={onEdit} onDelete={onDelete} />
)

export default TaskPage2024Task
